import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../i18n";

// Lightweight in-app tutorial overlay
// - Mobile-first, Tailwind only, green/white theme
// - No external deps
export default function Tutorial({ onClose, onNavigate }) {
	const { t, lang, setLang } = useI18n();
	const steps = useMemo(() => [
		{
			key: 'lang',
			section: 'dashboard',
			title: t('tutorial.lang.title'),
			text: t('tutorial.lang.text'),
			highlight: 'profile'
		},
		{
			key: 'welcome',
			section: 'dashboard',
			title: t('tutorial.welcome.title'),
			text: t('tutorial.welcome.text')
		},
		{
			key: 'dashboard',
			section: 'dashboard',
			title: t('tutorial.dashboard.title'),
			text: t('tutorial.dashboard.text')
		},
		{
			key: 'nav',
			section: 'dashboard',
			title: t('tutorial.nav.title') || 'Navigation',
			text: t('tutorial.nav.text') || 'Click the sidebar to explore sections. On mobile, use the bottom bar.',
			highlight: 'nav'
		},
		{
			key: 'orders',
			section: 'orders',
			title: t('tutorial.orders.title') || 'Orders',
			text: t('tutorial.orders.text') || 'Create and manage orders. Use the filters above to find orders quickly. Tap + Add Order to create a new one.',
			highlight: 'add-order'
		},
		// product step removed per request
		{
			key: 'expenses',
			section: 'expenses',
			title: t('tutorial.expenses.title') || 'Expenses',
			text: t('tutorial.expenses.text') || 'Track daily expenses. Tap + Add Expense to add a new expense.',
			highlight: 'add-expense'
		},
		{
			key: 'customers',
			section: 'customers',
			title: t('tutorial.customers.title'),
			text: t('tutorial.customers.text')
		},
		{
			key: 'accounts',
			section: 'accounts',
			title: t('tutorial.accounts.title'),
			text: t('tutorial.accounts.text')
		},
		{
			key: 'pl',
			section: 'pl',
			title: t('tutorial.pl.title') || 'Profit & Loss',
			text: t('tutorial.pl.text') || 'View profit trends and totals. Orders minus costs and expenses give your net profit.',
		},
		{
			key: 'done',
			section: 'dashboard',
			title: t('tutorial.done.title'),
			text: t('tutorial.done.text')
		}
	], [t]);

	const [idx, setIdx] = useState(0);
	const step = steps[idx] || steps[0];

	useEffect(() => {
		if (onNavigate && step?.section) {
			onNavigate(step.section);
		}
		// position highlight boxes for selector-based steps on next tick
		try {
			setTimeout(() => {
				const place = (selector, targetId) => {
					const el = document.querySelector(`[data-tour="${selector}"]`);
					const box = document.getElementById(targetId);
					if (!el || !box) return;
					const rect = el.getBoundingClientRect();
					box.style.left = `${rect.left - 6}px`;
					box.style.top = `${rect.top - 6}px`;
					box.style.width = `${rect.width + 12}px`;
					box.style.height = `${rect.height + 12}px`;
					box.style.borderRadius = '12px';
					box.style.boxShadow = '0 0 0 4px rgba(16,185,129,0.6)';
				};
				if (step.highlight === 'add-order') place('add-order', 'tp-highlight-add-order');
				if (step.highlight === 'add-expense') place('add-expense', 'tp-highlight-add-expense');
				if (step.highlight === 'nav') place('side-nav-toggle', 'tp-highlight-nav');
			}, 0);
		} catch {}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [idx]);

	const closeAndRemember = () => {
		try { localStorage.setItem('tp_onboarding_done', '1'); } catch {}
		onClose?.();
	};

	const next = () => {
		if (idx < steps.length - 1) setIdx(idx + 1);
		else closeAndRemember();
	};
	const prev = () => setIdx((i) => Math.max(0, i - 1));

	return (
		<div className="fixed inset-0 z-50">
			{/* Dim background */}
			<div className="absolute inset-0 bg-black/40" />

		{/* Optional highlights */}
		{step.highlight === 'nav' && (
			<div className="absolute inset-0 pointer-events-none">
				<div className="absolute" id="tp-highlight-nav" />
			</div>
		)}
			{step.highlight === 'fab' && (
				<div className="absolute right-6 bottom-24 w-14 h-14 rounded-full pointer-events-none">
					<span className="absolute inset-0 rounded-full bg-green-400/40 animate-ping" />
					<span className="absolute inset-0 rounded-full ring-4 ring-green-400/70" />
				</div>
			)}
		{step.highlight === 'add-order' && (
			<div className="absolute inset-0 pointer-events-none">
				{/* Simple selector-based highlight for add order */}
				<div className="absolute" id="tp-highlight-add-order" />
			</div>
		)}
		{step.highlight === 'add-expense' && (
			<div className="absolute inset-0 pointer-events-none">
				<div className="absolute" id="tp-highlight-add-expense" />
			</div>
		)}
			{step.highlight === 'profile' && (
				<div className="absolute top-4 right-6 w-8 h-8 rounded-full pointer-events-none">
					<span className="absolute inset-0 rounded-full bg-green-400/40 animate-ping" />
					<span className="absolute inset-0 rounded-full ring-4 ring-green-400/70" />
				</div>
			)}

			{/* Card */}
			<div className="absolute left-0 right-0 bottom-20 px-4">
			<div className="mx-auto max-w-md bg-white border border-green-100 rounded-2xl shadow-lg p-4">
					<div className="text-sm text-green-700 font-semibold mb-1 flex items-center justify-between">
						<span>{t('tutorial.quickTour')}</span>
						{/* Inline language switcher available during tour */}
						<div className="flex items-center gap-1">
							<button onClick={() => setLang('en')} className={`text-[11px] px-2 py-0.5 rounded ${lang === 'en' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 border border-green-200'}`}>EN</button>
							<button onClick={() => setLang('ta')} className={`text-[11px] px-2 py-0.5 rounded ${lang === 'ta' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 border border-green-200'}`}>TA</button>
						</div>
					</div>
					<div className="text-base font-bold mb-1">{step.title}</div>
					<div className="text-sm text-gray-700 mb-3">{step.text}</div>
					<div className="flex items-center justify-between">
						<button className="text-xs text-gray-500 px-2 py-1" onClick={closeAndRemember}>{t('tutorial.skip')}</button>
						<div className="flex items-center gap-2">
							<button
								className={`text-sm px-3 py-1.5 rounded ${idx === 0 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-white border border-green-200 text-green-700'}`}
								onClick={prev}
								disabled={idx === 0}
							>
								{t('tutorial.back')}
							</button>
							<button className="text-sm px-3 py-1.5 rounded bg-green-600 text-white" onClick={next}>
								{idx === steps.length - 1 ? t('tutorial.finish') : t('tutorial.next')}
							</button>
						</div>
					</div>
					<div className="mt-3 flex items-center gap-1 justify-center">
						{steps.map((_, i) => (
							<span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-green-600' : 'bg-green-200'}`} />
						))}
					</div>
				</div>
			</div>
		</div>
	);
}


